import React, { createContext, useCallback, useContext } from 'react';

import invariant from 'tiny-invariant';

import { fg } from '@atlaskit/platform-feature-flags';

import { useAnalyticsEvents } from '../../../common/analytics/generated/use-analytics-events';

import type {
	CollapseCallback,
	ExpandCallback,
	ResizeEndCallback,
	ResizeStartCallback,
	SideNavVisibilityContextType,
	SideNavVisibilityProviderProps,
} from './types';

export const SideNavVisibilityContext = createContext<SideNavVisibilityContextType | null>(null);

/*
 * useSideNavVisibility - Returns Visibility callbacks from Navigation System
 */
export const useSideNavVisibility = () => {
	const context = useContext(SideNavVisibilityContext);
	invariant(context, 'useSideNavVisibility must be used within an SideNavVisibilityProvider');

	return context;
};

/*
 *  SideNavVisibilityProvider - Wrapper for the Navigation System visibility callbacks.
 */
export function SideNavVisibilityProvider({
	children,
	onCollapse,
	onExpand,
	onResizeEnd,
	onResizeStart,
	// Remove when navx-noissue-20250918-fix-types is cleaned up
	setUserSideNavDataAsync,
	// Remove `Sync` suffix when navx-noissue-20250918-fix-types is cleaned up
	setUserSideNavDataSync,
}: SideNavVisibilityProviderProps) {
	const { fireEvent } = useAnalyticsEvents();

	const onCollapseHandler = useCallback<CollapseCallback>(
		(args) => {
			fireEvent('ui.sideNav.collapsed', { ...args });
			fg('navx-noissue-20250918-fix-types')
				? setUserSideNavDataSync({ defaultCollapsed: true })
				: setUserSideNavDataAsync({ defaultCollapsed: true });
			onCollapse?.(args);
		},
		[fireEvent, onCollapse, setUserSideNavDataAsync, setUserSideNavDataSync],
	);

	const onExpandHandler = useCallback<ExpandCallback>(
		(args) => {
			fireEvent('ui.sideNav.expanded', { ...args });
			fg('navx-noissue-20250918-fix-types')
				? setUserSideNavDataSync({ defaultCollapsed: false })
				: setUserSideNavDataAsync({ defaultCollapsed: false });
			onExpand?.(args);
		},
		[fireEvent, onExpand, setUserSideNavDataAsync, setUserSideNavDataSync],
	);

	const onResizeStartHandler = useCallback<ResizeStartCallback>(
		(args) => {
			fireEvent('ui.sideNav.resizeStart', { ...args });
			onResizeStart?.(args);
		},
		[fireEvent, onResizeStart],
	);

	const onResizeEndHandler = useCallback<ResizeEndCallback>(
		(args) => {
			fireEvent('ui.sideNav.resizeEnd', { ...args });
			fg('navx-noissue-20250918-fix-types')
				? setUserSideNavDataSync({ defaultWidth: args.finalWidth })
				: setUserSideNavDataAsync({ defaultWidth: args.finalWidth });
			onResizeEnd?.(args);
		},
		[fireEvent, onResizeEnd, setUserSideNavDataAsync, setUserSideNavDataSync],
	);

	return (
		<SideNavVisibilityContext.Provider
			value={{
				onExpand: onExpandHandler,
				onCollapse: onCollapseHandler,
				onResizeEnd: onResizeEndHandler,
				onResizeStart: onResizeStartHandler,
			}}
		>
			{children}
		</SideNavVisibilityContext.Provider>
	);
}
